import React from 'react';
import { Flex, Text, Button, IconButton, Icon, Th } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

// ── Sort indicator icon ──
function SortIcon({ direction }: { direction: 'asc' | 'desc' | null }) {
  if (!direction) {
    return (
      <Icon viewBox="0 0 14 14" w={3} h={3} ml={1} color="gray.400" aria-hidden>
        <path d="M7 2l3 4H4l3-4zm0 10L4 8h6l-3 4z" fill="currentColor" />
      </Icon>
    );
  }
  return (
    <Icon viewBox="0 0 14 14" w={3} h={3} ml={1} color="brand.500" aria-hidden>
      {direction === 'asc'
        ? <path d="M7 2l4 5H3l4-5z" fill="currentColor" />
        : <path d="M7 12L3 7h8l-4 5z" fill="currentColor" />}
    </Icon>
  );
}

// ── Clickable sortable table header ──
export function SortableTh({
  column, currentSort, currentOrder, onSort, isNumeric, children,
}: {
  column: string; currentSort: string; currentOrder: string;
  onSort: (column: string) => void; isNumeric?: boolean; children: React.ReactNode;
}) {
  const isActive = currentSort === column;
  const direction = isActive ? (currentOrder as 'asc' | 'desc') : null;
  return (
    <Th isNumeric={isNumeric} cursor="pointer" userSelect="none" onClick={() => onSort(column)} _hover={{ color: 'brand.500' }} whiteSpace="nowrap">
      <Flex align="center" display="inline-flex" gap={1}>
        {children}
        <SortIcon direction={direction} />
      </Flex>
    </Th>
  );
}

// ── Chevron icon for pagination ──
function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <Icon viewBox="0 0 16 16" w={4} h={4}>
      {direction === 'left'
        ? <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        : <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />}
    </Icon>
  );
}

// ── Page number generator (1 ... 4 5 6 ... 10) ──
// eslint-disable-next-line react-refresh/only-export-components
export function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

// ── Pagination footer ──
export function DataTablePagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  if (totalCount === 0) return null;

  return (
    <Flex justify="space-between" align="center" px={6} py={4} borderTop="1px" borderColor="gray.100">
      <Text fontSize="sm" color="gray.500">
        {t('admin.library.pagination.showing', 'Showing')} {rangeStart} {t('admin.library.pagination.to', 'to')} {rangeEnd} {t('admin.library.pagination.of', 'of')} {totalCount} {t('admin.library.pagination.entries', 'entries')}
      </Text>
      <Flex gap={1}>
        <IconButton
          aria-label={t('admin.library.pagination.previous', 'Previous page')}
          icon={<ChevronIcon direction="left" />}
          size="sm"
          variant="outline"
          borderRadius="10px"
          isDisabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          data-testid="pagination-prev"
        />
        {getPageNumbers(page, totalPages).map((p, i) =>
          p === '...' ? (
            <Text key={`ellipsis-${i}`} px={2} lineHeight="32px" fontSize="sm" color="gray.400">…</Text>
          ) : (
            <Button
              key={p}
              size="sm"
              variant={p === page ? 'solid' : 'outline'}
              colorScheme={p === page ? 'brand' : 'gray'}
              borderRadius="10px"
              minW="32px"
              onClick={() => onPageChange(p as number)}
              data-testid={`pagination-page-${p}`}
            >
              {p}
            </Button>
          ),
        )}
        <IconButton
          aria-label={t('admin.library.pagination.next', 'Next page')}
          icon={<ChevronIcon direction="right" />}
          size="sm"
          variant="outline"
          borderRadius="10px"
          isDisabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          data-testid="pagination-next"
        />
      </Flex>
    </Flex>
  );
}

// ── Filter icon for toggle button ──
export function FilterIcon() {
  return (
    <Icon viewBox="0 0 16 16" w={4} h={4}>
      <path d="M2 3h12M4 7h8M6 11h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    </Icon>
  );
}
